import { cva, type VariantProps } from 'class-variance-authority'
import { ComponentProps, forwardRef, ReactNode } from 'react'

import { cn } from '../../lib/utils'

const buttonVariants = cva(
    'flex items-center justify-center font-semibold rounded gap-2 disabled:cursor-not-allowed',
    {
        variants: {
            variant: {
                primary: 'bg-chart-ok-medium text-white hover:bg-chart-ok-dark',
                destructive: 'bg-chart-alert-medium text-white hover:bg-chart-alert-dark',
                outlined:
                    'bg-primary-foreground border border-gray-dark text-gray-dark hover:bg-gray-light disabled:border-gray-medium disabled:text-gray-medium',
                ghost: 'bg-transparent text-gray-dark hover:bg-gray-light',
                ghostGreen: 'bg-transparent text-chart-ok-dark hover:bg-chart-ok-light',
                link: 'bg-transparent text-chart-ok-medium underline hover:text-chart-ok-dark',
                ghostLink: 'bg-transparent text-gray-medium underline hover:text-gray-dark',
            },
            size: {
                xs: 'text-xs px-2 py-1',
                sm: 'text-sm px-3 py-2',
                md: 'text-base px-4 py-2',
                lg: 'text-lg px-5 py-3',
                xl: 'text-xl px-6 py-3',
                '2xl': 'text-2xl px-7 py-4',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'md',
        },
    },
)

type ButtonProps = ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        onClick: () => void
        children: ReactNode
        leftIcon?: ReactNode
        rightIcon?: ReactNode
        isDisabled?: boolean
    }

export const ChartButton = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant,
            size,
            onClick,
            children,
            leftIcon,
            rightIcon,
            isDisabled = false,
            className,
            ...rest
        },
        ref,
    ) => {
        return (
            <button
                ref={ref}
                type="button"
                disabled={isDisabled}
                className={cn(buttonVariants({ variant, size }), className)}
                onClick={onClick}
                {...rest}
            >
                {leftIcon && <span>{leftIcon}</span>}
                {children}
                {rightIcon && <span>{rightIcon}</span>}
            </button>
        )
    },
)

ChartButton.displayName = 'ChartButton'
